import { type AxiosError } from 'axios';

import { getEnvironment, IsAnyTLDEnvironment } from './environment';

export interface URLSchema {
    api: string;
    websocket: string;
    vanityUrl: string;
    openLms: string;
    openLmsMoodle: string;
    moodle: string;
    webGlobal: string;
    apiGlobal: string;
    lb: string;
}

export const parseUrlState = (state): any => {
    let params = {};

    if (!state || typeof state !== 'string') {
        return params;
    }

    try {
        params = JSON.parse(state);
    } catch (error) {
        try {
            params = JSON.parse(decodeURIComponent(state));
        } catch (err) {
            state.split('/').forEach((stateParam) => {
                const [name, value] = stateParam.split(':');
                params[name] = value;
            });
        }
    }

    return params;
};

/**
 * Get all parameters from the URL query.
 */
export const getUrlParams = (): Record<string, string | undefined> => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchParamsObj = Object.fromEntries(searchParams.entries());

    return searchParamsObj;
};

/**
 * Get a parameter from the URL query.
 */
export function getUrlParam(name: string, required: true): string;
export function getUrlParam(name: string, required?: boolean): string | undefined;
export function getUrlParam(name: string, required: boolean = false): string | undefined {
    const params = getUrlParams();

    if (required && !params[name]) {
        throw new Error(`Missing required URL parameter: ${name}`);
    }

    return params[name];
}

/**
 * Set all parameters in the URL query.
 */
export const setUrlParams = (searchParams: URLSearchParams) => {
    window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}`);
};

/**
 * Get a parameter from the URL query and immediately remove it from the URL for security reasons.
 */
export const popUrlParam = (name: string): string | undefined => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchParam = getUrlParam(name);

    if (searchParam === undefined) {
        return;
    }

    searchParams.delete(name);
    setUrlParams(searchParams);

    return searchParam;
};

export const isDevelopmentEnv = () => {
    const { region, environment } = getEnvironment();
    return region === 'local' || environment === 'dev';
};

export const getUrls = (): URLSchema => {
    const { account, pod, region, environment } = getEnvironment();

    let websocket: string;
    let vanityUrl: string;
    let suffix: string;

    if (region === 'local') {
        suffix = `${pod}.${region}.dev.${process.env.REACT_APP_DOMAIN_NAME}`;
        websocket = `wss://websockets.${pod}.${region}.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`;
        websocket = `wss://websockets.pod-1.us-east-1.test.class.com`;
        vanityUrl = `https://${account}.web.${suffix}`;
    } else {
        suffix = `${pod}.${region}.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`;
        websocket = `https://websockets.${suffix}`;
        vanityUrl = IsAnyTLDEnvironment()
            ? `https://${account}.${process.env.REACT_APP_DOMAIN_NAME}`
            : `https://${account}.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`;
    }

    const apiPath = process.env.REACT_APP_API_PATH || `https://${account}.rest.${suffix}`;

    return {
        api: apiPath,
        websocket,
        vanityUrl,
        openLms: `https://${account}-${environment}.lms.${process.env.REACT_APP_DOMAIN_NAME}`,
        openLmsMoodle: `https://${account}-${environment}.lms.${process.env.REACT_APP_DOMAIN_NAME}/moodle`,
        moodle: `https://${account}.moodle.${pod}.${region}.${environment}.${process.env.REACT_DOMAIN_FOUR}/moodle`,
        webGlobal: `https://main.web.global.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`,
        apiGlobal: `https://main.rest.global.${environment}.${process.env.REACT_APP_DOMAIN_NAME}`,
        lb: `https://${account}.lb.${suffix}`
    };
};

/**
 * Process an apiPath returning the full url and optionally interpolating key values in the path.
 */
export const processApiPath = (
    apiPath: string,
    keyValues?: Record<string, string>,
    queryParams?: Record<string, string>
): string => {
    const { api } = getUrls();
    let apiPathProcessed = apiPath;
    if (keyValues) {
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of Object.entries(keyValues)) {
            const value = encodeURIComponent(val);
            const pathComp = `{${key}}`;
            if (apiPath.indexOf(pathComp) === -1) {
                throw Error(`API path does not contain component key: ${pathComp}`);
            }
            apiPathProcessed = apiPathProcessed.replace(pathComp, value);
        }
    }

    const qs = queryParams ? `?${new URLSearchParams(queryParams)}` : '';
    return `${api}${apiPathProcessed}${qs}`;
};

enum ReponseError {
    ERR_NO_RESPONSE = 1000,
    ERR_REQUEST = 1001
}

export const getErrorCode = (err: AxiosError): number => {
    if (err.response) {
        return err.response.status;
    }

    if (err.request) {
        return ReponseError.ERR_NO_RESPONSE;
    }

    return ReponseError.ERR_REQUEST;
};
